import { computed, nextTick, ref } from 'vue'
import type { Router, RouteLocationNormalizedLoaded } from 'vue-router'
import { bookApi, readingApi, userApi } from '@/api'
import type { Book, Chapter } from '@/types/user'
import type { ReadingChapter } from '@/types/reading'
import type { useAuthStore } from '@/stores/auth'

type AuthStore = ReturnType<typeof useAuthStore>

type ReadingDataHooks = {
  afterChapterLoaded?: () => void
  afterBookLoaded?: () => void
  checkAIServiceStatus?: () => void
}

const createEmptyBook = (): Book => ({
  id: 0,
  title: '',
  author: '',
  coverUrl: '',
  description: '',
  category: 'general',
  tags: [],
  totalChapters: 0,
  viewCount: 0,
  isAiGenerated: false,
  status: 'published',
  createdAt: '',
  updatedAt: ''
})

const createEmptyChapter = (): ReadingChapter => ({
  id: 0,
  bookId: 0,
  chapterNumber: 1,
  title: '',
  content: '',
  isFree: true,
  price: 0,
  wordCount: 0,
  createdAt: '',
  updatedAt: '',
  hasAccess: true,
  accessReason: '免费章节'
})

export const useReadingData = (options: {
  route: RouteLocationNormalizedLoaded
  router: Router
  authStore: AuthStore
}) => {
  const isLoading = ref(true)
  const error = ref<string | null>(null)
  const book = ref<Book>(createEmptyBook())
  const chapters = ref<Chapter[]>([])
  const chapter = ref<ReadingChapter>(createEmptyChapter())
  const currentChapter = ref(1)
  const isInBookshelf = ref(false)
  const isAddingToBookshelf = ref(false)
  const existingShelfBook = ref<Book | null>(null)
  const showExistingShelfDialog = ref(false)
  const hooks: ReadingDataHooks = {}

  const isFreshPublicReading = computed(() => {
    return book.value.visibility === 'public' && options.route.query.new === '1'
  })

  const isShelfEntryRoute = computed(() => {
    return options.route.query.from === 'shelf'
  })

  const defaultForkTitle = computed(() => {
    if (!book.value.title) return '我的新剧情'
    return `${book.value.title}（2）`
  })

  const isPublicUnshelvedReading = computed(() => {
    return options.authStore.isAuthenticated && book.value.visibility === 'public' && (!isInBookshelf.value || isFreshPublicReading.value)
  })

  const shouldPersistReadingProgress = computed(() => {
    if (!options.authStore.isAuthenticated) return false
    if (isFreshPublicReading.value) return false
    if (book.value.visibility === 'private') return true
    return isInBookshelf.value
  })

  const setReadingDataHooks = (nextHooks: ReadingDataHooks) => {
    Object.assign(hooks, nextHooks)
  }

  const findChapterIdByNumber = (chapterNumber: number): number | null => {
    const found = chapters.value.find(ch => ch.chapterNumber === chapterNumber)
    return found?.id || null
  }

  const checkBookshelfStatus = async () => {
    if (!options.authStore.isAuthenticated) return

    try {
      const response = await userApi.getBookshelf({ limit: 100 })
      if (response.code === 200 && response.data?.items) {
        isInBookshelf.value = response.data.items.some(item => item.id === book.value.id)
        existingShelfBook.value = response.data.items
          .filter(item => item.id === book.value.id || (item.sourceBookId === book.value.id && item.visibility === 'private'))
          .sort((a, b) => new Date(b.updatedAt || b.createdAt).getTime() - new Date(a.updatedAt || a.createdAt).getTime())[0] || null
      }
    } catch (err) {
      console.error('检查书架状态失败:', err)
    }
  }

  const loadChapterContent = async () => {
    try {
      const chapterId = findChapterIdByNumber(currentChapter.value)

      if (!chapterId) {
        chapter.value = {
          ...chapter.value,
          title: `第${currentChapter.value}章`,
          content: '⚠️ 本章内容尚未生成。请使用 AI 续写功能创建后续章节。',
          hasAccess: true,
          accessReason: '内容未生成'
        }
        return
      }

      const response = await readingApi.getChapter(chapterId)
      if (response.code === 200 && response.data?.chapter) {
        chapter.value = {
          ...response.data.chapter,
          accessReason: (response.data.chapter as ReadingChapter).accessReason || '已解锁'
        }
        await nextTick()
        hooks.afterChapterLoaded?.()

        if (shouldPersistReadingProgress.value) {
          try {
            await readingApi.startReading({
              bookId: book.value.id,
              chapterId
            })
          } catch (err) {
            console.error('记录阅读开始失败:', err)
          }
        }
      }
    } catch (err: any) {
      console.error('加载章节内容失败:', err)

      if (err.response?.data?.code === 403 && err.response?.data?.data?.needPayment) {
        const paymentData = err.response.data.data
        chapter.value = {
          ...chapter.value,
          title: `第${currentChapter.value}章`,
          content: '',
          hasAccess: false,
          accessReason: 'need_payment',
          price: paymentData.price || 5,
          unlockPack: paymentData.pack
        }
      } else {
        chapter.value = {
          ...chapter.value,
          title: `第${currentChapter.value}章`,
          content: `⚠️ 章节加载失败：${err.message || '请检查网络连接后重试'}`,
          hasAccess: true,
          accessReason: '加载失败'
        }
      }
    }
  }

  const loadBookData = async () => {
    try {
      isLoading.value = true
      error.value = null

      const bookId = parseInt(options.route.params.bookId as string)
      const chapterNumber = parseInt(options.route.params.chapterId as string) || 1

      const bookResponse = await bookApi.getBook(bookId)
      if (bookResponse.code === 200 && bookResponse.data?.book) {
        book.value = bookResponse.data.book
      } else {
        throw new Error('书籍信息加载失败')
      }

      const chaptersResponse = await bookApi.getBookChapters(bookId, { limit: 100 })
      if (chaptersResponse.code === 200 && chaptersResponse.data?.items) {
        chapters.value = chaptersResponse.data.items
        console.log('加载章节列表成功:', chapters.value.length, '个章节')
      } else {
        console.warn('章节列表加载失败或为空')
      }

      await checkBookshelfStatus()

      currentChapter.value = chapterNumber
      if (book.value.visibility === 'public' && existingShelfBook.value && options.route.query.new !== '1' && !isShelfEntryRoute.value) {
        showExistingShelfDialog.value = true
      }

      await loadChapterContent()
      hooks.afterBookLoaded?.()

      setTimeout(() => {
        hooks.checkAIServiceStatus?.()
      }, 500)
    } catch (err: any) {
      error.value = err.message || '加载失败，请重试'
      console.error('加载书籍数据失败:', err)
    } finally {
      isLoading.value = false
    }
  }

  const addToBookshelf = async () => {
    if (!options.authStore.isAuthenticated) {
      options.router.push('/login')
      return
    }

    try {
      isAddingToBookshelf.value = true
      const response = await userApi.addToBookshelf(book.value.id)

      if (response.code === 200) {
        isInBookshelf.value = true
      }
    } catch (err: any) {
      console.error('加入书架失败:', err)
      alert(err.message || '加入书架失败，请重试')
    } finally {
      isAddingToBookshelf.value = false
    }
  }

  const saveCurrentReadingToBookshelf = async (saveForkTitle: string) => {
    if (isFreshPublicReading.value) {
      const response = await bookApi.forkReading(book.value.id, {
        forkTitle: saveForkTitle.trim() || defaultForkTitle.value,
        currentChapter: currentChapter.value
      })

      if (response.code === 200 && response.data?.book) {
        isInBookshelf.value = true
        return
      }

      throw new Error(response.message || '另存失败')
    }

    await userApi.addToBookshelf(book.value.id)
    isInBookshelf.value = true

    const chapterId = findChapterIdByNumber(currentChapter.value)
    if (!chapterId) return

    await readingApi.startReading({
      bookId: book.value.id,
      chapterId
    })

    await readingApi.updateProgress({
      bookId: book.value.id,
      chapterId,
      progress: (currentChapter.value / book.value.totalChapters) * 100
    })
  }

  return {
    isLoading,
    error,
    book,
    chapters,
    chapter,
    currentChapter,
    isInBookshelf,
    isAddingToBookshelf,
    existingShelfBook,
    showExistingShelfDialog,
    isFreshPublicReading,
    isShelfEntryRoute,
    defaultForkTitle,
    isPublicUnshelvedReading,
    shouldPersistReadingProgress,
    setReadingDataHooks,
    loadBookData,
    loadChapterContent,
    findChapterIdByNumber,
    addToBookshelf,
    saveCurrentReadingToBookshelf
  }
}
